import { LightningElement, track, api  } from 'lwc';
import {iconPath, publicPropertyToBool} from '../../../utils/basicUtils.js';
import { handleReadOnlySearch } from '../../../utils/comboboxUtils.js';


/* Example Usage: 
     <x-slds-combobox-multi
        place-holder={field.placeHolder}
        current-value={field.value}
        options={field.options}
        label={field.label}
        oncombomultiselection={handleNewInput}
        data-id={field.fieldName}
        data-element="fieldInput"
    ></x-slds-combobox-multi>
*/



export default class sldsComboboxMulti extends LightningElement {
    static renderMode = 'light';
    groupId = 3000;
    pillIdCnt = 9000;
    idCnt = 300;
    stableId = 39393483;

    @track isRendered = false;
    @track isGrouped = false;
    @track selectionsError = null;
    @track hasError = false;

    @api suppressFlip = false;

    // --------- Tracked HTML Elements --------- \\
    @track _dropdownTrigger; // Controls open/close state
    @track _comboOptions;   // List of menu options
    @track _inputEle;       // <input> element
    _groupHeaders;
    // ----------------------------------------- \\

    @track pillSelections = [];
    // ======================= Public Properties ========================= \\
    // Label for the combobox
    @api label = '';
    @api currentValue;
    _readOnlyStr = '';
    _readOnlyStrTimeStamp;

    // ---------- Placeholder ---------- \\
    @track _placeHolder;
    @api
    set placeHolder(val) {
        this._placeHolder = val;
    }
    get placeHolder() {
        return this._placeHolder;
    }

    // --------- Required Bool --------- \\
    @api set isRequired(val) {this._isRequired = publicPropertyToBool(val);}
    get isRequired() {return this._isRequired;}


    // ------ List of Options (Regular) ------ \\
    @track _options;
    @api 
    set options(val) {
        this._options = val.map(option => {
            return option.icon 
                ? { 
                    ...option, 
                    stableId: option.stableId || `${this.stableId++}`, 
                    icon: iconPath(option.icon, true), 
                    id: `option-id-${this.idCnt++}`, 
                    isMeta: Object.keys(option).some(k => k.startsWith('meta')) 
                }
                : {
                    ...option, 
                    stableId: option.stableId || `${this.stableId++}`, 
                    id: `option-id-${this.idCnt++}`, 
                    isMeta: Object.keys(option).some(k => k.startsWith('meta'))
                };
        });
    }
    get options() {
        return this._options;
    }

    // ----- List of Options (Grouped) ----- \\
    @api 
    set groupedOptions(val) {
        // Flag the grouped options
        this.isGrouped = true;
        // Add custom properties the group and group-options 
        this._groupedOptions = val.map(group => {
            return {
                label: group.label,
                groupId: `${group.label}-${this.groupId++}`,
                options: group.options.map(option => {
                    // Define base grouped option
                    let groupedOption = {
                        ...option,
                        stableId: option.stableId || `${this.stableId++}`, 
                        id: `option-id-${this.idCnt++}`, 
                        isMeta: Object.keys(option).some(k => k.startsWith('meta')),
                        group: group.label
                    }
                    
                    if (groupedOption.icon) {
                        // Add icon if parent included icons
                        groupedOption.icon = typeof groupedOption.icon === 'string'
                            ? iconPath(groupedOption.icon, true)
                            : groupedOption.icon;
                    }
                    
                    return groupedOption 
                })
            };
        });
    }
    get groupedOptions() {
        return this._groupedOptions;
    }

    // -------- Selection Amount (Min) -------- \\
    @api // Get the minimum required selections (if any)
    set minSelections(val) {
        this._minSelections = val ? parseInt(val) : null;
    }
    get minSelections() {
        return this._minSelections;
    }
    
    // -------- Selection Amount (Max) -------- \\
    @api 
    set maxSelections(val) {
        this._maxSelections = val ? parseInt(val) : null;
    }
    get maxSelections() {
        return this._maxSelections;
    }

    // --------- Is Popover (Boolean) --------- \\
    @api 
    set isPopover(val) {this._isPopover = publicPropertyToBool(val);}
    get isPopover() {return this._isPopover;}


    // ---------- Is Double Combobox ---------- \\
    @api // Use the "slds-combobox-addon_end" variant if is a double-combobox
    set isAddon(val) {
        this._isAddon = val &&  (val === 'true' || val === true);
    }
    get isAddon() {
        return this._isAddon;
    }
    // =================================================================== \\

    @api focusMe() {
        const comboboxInput = this.querySelector('[data-id="inputEle"]');
        comboboxInput.focus();
    }
    @api errorMe(errMsg=null) {
        this.selectionsError = errMsg || 'This field is required';
        this.hasError = true;
    }
    @api removeErrorMe() {
        this.selectionsError = null;
        this.hasError = false;
    }
    @api getValue() {
        return this.pills.map(pill => (pill.value));
    }

    // Check if selections have error
    updatePillErrorStatus() {
        // Add "too much" selections error message
        if (this.maxSelections && (this.pillSelections && this.pillSelections.length > this.maxSelections)) {
            this.querySelector('[data-id="formElement"]').classList.add('slds-has-error');
            this._inputEle.setAttribute("aria-describedby", "error-message-unique-id");
            this._inputEle.setAttribute("aria-invalid", "true");
            this.isEvent = false;
            this.selectionsError = `Only ${this._maxSelections} selections allowed`;
            return;
        }
        // Remove the "too much" error
        if (this.selectionsError && this.pillSelections.length <= this.maxSelections) {
            this.querySelector('[data-id="formElement"]').classList.remove('slds-has-error');
            this._inputEle.removeAttribute("aria-describedby");
            this._inputEle.setAttribute("aria-invalid", "false");
            this.selectionsError = null;
        }

        // Add "not enough selections" error message
        if (this.minSelections && (this.pillSelections.length < this.minSelections)) {
            this.querySelector('[data-id="formElement"]').classList.add('slds-has-error');
            this._inputEle.setAttribute("aria-describedby", "error-message-unique-id");
            this._inputEle.setAttribute("aria-invalid", "true");
            this.isEvent = false;
            this.selectionsError = `${this.minSelections} selections required`;
        }
        // Remove the "not enough selections" error 
        if (this.selectionsError && this.pillSelections.length >= this.minSelections) {
            this.querySelector('[data-id="formElement"]').classList.remove('slds-has-error');
            this._inputEle.removeAttribute("aria-describedby");
            this._inputEle.setAttribute("aria-invalid", "false");
            this.selectionsError = null;
        }
    }
    // Return flattened grouped options to the handlers if <isGrouped>
    flatGroupedOptions() {
        if (!this.isGrouped) return;
        return this.groupedOptions.map(group => group.options).flat();
    }
    // Set option to  isSelected=true when selected
    selectOption(selectedOption, stableId) {
        let isSelected = false;
        // Allow id to be passed to the function
        const dyanmicOption = typeof selectedOption === 'string' 
            ? {fake: 'one', id: selectedOption} 
            : selectedOption;


        // Handle Grouped Options
        if (this.isGrouped) {
            this.groupedOptions = this.groupedOptions.map(group => {
                return group.options.some(opt => opt.id === dyanmicOption.id)
                    ? {...group, 
                        options: group.options.map(opt => {
                            if (opt.id === dyanmicOption.id && !opt.isSelected) {
                                isSelected = true;
                            }
                            return opt.id === dyanmicOption.id 
                                ? {...opt, isSelected: opt.isSelected ? false : true} 
                                : opt;
                        }) 
                    }
                    : group;
            });
            // Update Pills
            this.pillSelections = this.groupedOptions.map(group => group.options).flat().map(opt => {
                return opt.isSelected ? {...opt, keyId: `${this.pillIdCnt++}`} : null;
            }).filter(Boolean);
        } else {
            // Handle Regular Options
            this._options = this._options.map(opt => {
                if (opt.id === dyanmicOption.id) {
                    isSelected = !opt.isSelected;
                }
                return opt.id === dyanmicOption.id 
                    ? {...opt, isSelected: isSelected}
                    : opt;
            });
            // Update Pills
            this.pillSelections = this._options.filter(opt => opt.isSelected);
        }

        /* Update class & aria attributes
        setTimeout(() => {
            this._comboOptions.forEach(combo => {
                if (combo.dataset.stableId === stableId) {
                    combo.classList.add('slds-has-focus');
                    if (isSelected) {
                        combo.setAttribute("aria-selected", "true");
                    } else {
                        combo.setAttribute("aria-selected", "false");
                    }
                }
            });
        });*/
    }
    // Remove a selected option
    removeOption(event) {
        // Get the constant id
        const stableId = event.currentTarget.dataset.stableId;

        if (this.isGrouped) {
            // Replace the _groupedOptions array with a new reference
            const updatedGroups = this._groupedOptions.map(group => {
                const updatedOptions = group.options.map(opt => {
                    if (opt.stableId === stableId) {
                        return { ...opt, isSelected: false };
                    }
                    return opt;
                });
                return { ...group, options: updatedOptions };
            });

            this._groupedOptions = [...updatedGroups];  // Trigger reactivity
        } else {
            this._options = this._options.map(opt => {
                if (opt.stableId === stableId) {
                    return { ...opt, isSelected: false };
                }
                return opt;
            });
        }
        
        // Force a reactivity update for pills
        this.pillSelections = this.pills;

        // Update pills error message
        this.updatePillErrorStatus();
    }
    // Add/Remove pills as options are selected/unselected
    get pills() {
        // Handle Grouped options array
        if (this._groupedOptions && this._groupedOptions.length) {
            if (this.flatGroupedOptions().some(opt => opt.isSelected)) {
                return this.flatGroupedOptions().map(opt => {
                    return opt.isSelected 
                        ? {...opt, keyId: `${this.pillIdCnt++}`}
                        : null;
                }).filter(Boolean);
            }
            return [];
        }

        // Handle Regular options array
        if (
            this._options && this._options.length && 
            this._options.some(opt => opt.isSelected)
        ) {
            return this._options.map(opt => { 
                return opt.isSelected 
                    ? {...opt, keyId: `${this.pillIdCnt++}`} 
                    : null; 
            }).filter(Boolean);
        }
        return [];    
    }

    // Track HTML Elements: <this._dropdownTrigger>, <this._comboOptions>, <this._inputEle>
    renderedCallback() {
        if (!this.isRendered && this.currentValue) {
            if (!this.isGrouped) {
                let valsSet;
                if (Array.isArray(this.currentValue)) valsSet = new Set(this.currentValue);
                this._options = this._options.map(opt => {
                    return (valsSet && valsSet.has(opt.value)) || (!valsSet && opt.value === this.currentValue)
                        ? {...opt, isSelected: true}
                        : opt;
                });
            }   
            this.pillSelections = this._options.filter(opt => opt.isSelected);
            this.isRendered = true;
        }
        if (!this._dropdownTrigger) {
            // Get dropdownTrigger (aria-expanded && slds-is-open)
            const dropdownTrigger = this.querySelector('.slds-dropdown-trigger');
            if (dropdownTrigger) {
                this._dropdownTrigger = dropdownTrigger;
            }
        }

        
        // Get Combobox Options (slds-has-focus --fake focus--)
        const comboOptions = this.querySelectorAll('.slds-listbox__option_entity');
        if (comboOptions) {
            this._comboOptions = comboOptions;
        }
    
    
        
        if (!this._inputEle) {
            // Get <input> element
            const inputEle = this.querySelector('[data-id="inputEle"]');
            if (inputEle) {
                this._inputEle = inputEle;
            }
        }

        if (this.isGrouped && !this._groupHeaders) {
            // Get the Group Header elements 
            const groupHeaders = this.querySelectorAll('[data-element="groupHeader"]')
            this._groupHeaders = groupHeaders;
        }
    
     
    }

    // ============================ Getters ============================== \\
    // Set flag for html-rendering of icons ---++ Update options with the iconContainer/Path
    get isIcons() {
        return this.isGrouped 
            ? this.flatGroupedOptions().some(opt => opt.hasOwnProperty('icon'))
            : this._options.some(opt => opt.hasOwnProperty('icon'));
    }
    // Add slds-listbox__option_plain if no icon in the options
    get listBoxOptionClass() {
        if (this.isGrouped) {
            return this.flatGroupedOptions().some(opt => opt.hasOwnProperty('icon')) 
                ? 'slds-media slds-listbox__option slds-listbox__option_plain slds-media_small slds-media_center'
                : 'slds-media slds-media_small slds-listbox__option slds-listbox__option_entity slds-listbox__option_has-meta slds-listbox__option_plain';    
        }
        return this._options.some(opt => opt.hasOwnProperty('icon')) 
            ? 'slds-media slds-listbox__option slds-listbox__option_entity slds-listbox__option_has-meta slds-media_center'
            : 'slds-media slds-listbox__option slds-listbox__option_entity slds-listbox__option_has-meta slds-listbox__option_plain';
    }
    // Display the length of the selected options followed by 'options selected'
    get inputValue() {
        if (!this.minSelections) {
            return this.pillSelections.length
                ? `${this.pillSelections.length} options selected`
                : '';
        } 
        return `${this.pillSelections.length} of ${this.minSelections} selected`;
    }
    // Add slds-has-error is <this.hasError> is true
    get formElementClass() {
        return this.hasError 
            ? "slds-form-element slds-has-error"
            : "slds-form-element";
    }
    // =================================================================== \\


    // ==================== Combobox State Handlers ====================== \\
    // Close Combobox && focus input
    closeCombobox() {
        const val = this._value;
        if (this._dropdownTrigger) {
            this._dropdownTrigger.classList.remove('slds-is-open');
            this._dropdownTrigger.setAttribute("aria-expanded", "false");
            this._comboOptions.forEach(opt => {
                opt.classList.remove('slds-has-focus');
                opt.setAttribute('aria-selected', 'false');
            });
        }

        // Cancel the event if min selections error
        this.updatePillErrorStatus();

        if (!this.selectionsError) {
            // Cancel the min selections error if event  
            if (this.selectionsError) this.selectionsError = null;

            this.isEvent = false;
            this.dispatchEvent(new CustomEvent('combomultiselection', {
                detail: this.pills,
                bubbles: true,
                composed: true
            }));
        }
    }
    // Open Combobox
    openCombobox() {
        this._readOnlyStr = '';
        if (this._dropdownTrigger) {
            this._dropdownTrigger.classList.add('slds-is-open');
            this._dropdownTrigger.setAttribute("aria-expanded", "true");
        }
    }
    // =================================================================== \\

    // open/close the combobox
    handleInputClick(event) {
        if (!this._dropdownTrigger.classList.contains('slds-is-open')) {
            this.openCombobox();
            return;
        } else {
            event.stopPropagation();
            this.closeCombobox();
        }
    }


    // =================== Listbox Options Handlers ====================== \\
    // Set "aria-activedescendant" on the inputEle && aria-selected="true" on the option 
    handleFauxFocusOption(optionId) {
        try {            
            let cnt = 0;
            this._comboOptions.forEach(option => {
                if (option.id === optionId) {
                    option.setAttribute('aria-selected', 'true');
                    option.classList.add('slds-has-focus');
                    if (this._groupHeaders && cnt === 0) {this._groupHeaders[0].scrollIntoView({behavior: 'smooth', block: 'nearst'});}
                    else {option.scrollIntoView({behavior: 'smooth', block: 'nearest'})};
                } else {
                    option.setAttribute('aria-selected', 'false');
                    option.classList.remove('slds-has-focus');
                }
                cnt++;
            });
        }catch(error) {
            console.error('handleFauxFocusOption', error);
        }
    }
    // Don't let focus leave the input element
    deflectFocus(event)  {
        event.preventDefault(); 
    }
    // Set the value to the clicked options value
    handleOptionClick(event) {
        const optionId = event.currentTarget.id;
        event.preventDefault();
        // Mark option as selected
        this.selectOption(optionId, event.currentTarget.dataset.stableId);
    }
    // =================================================================== \\


    // Close the combobox Tab/Escape --> Open if Enter
    handleKeyDown(event) {
        if (event.key === 'Tab' || event.key === 'Escape') {
            if (event.key === 'Escape') event.stopPropagation();
            // Close combobox on 'Escape'/'Tab'
            this.closeCombobox();
            return;
        }
        
        if (event.key === 'Enter') {
            if (this._dropdownTrigger.classList.contains('slds-is-open')) {
                // Handle new selection
                event.stopPropagation();
                const selected = Array.from(this._comboOptions).find(opt => opt.classList.contains('slds-has-focus')); 
                if (selected) {
                    this.isEvent = true;
                    this.selectOption(selected.id, selected.dataset.stableId);
                }
                return;
            } 
            // Open combobox
            this.openCombobox();
        }

        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            // Find the next combobox to be focused
            const isDown = event.key === 'ArrowDown';
            const arrLength = Array.from(this._comboOptions).length;

            const currentSelected = Array.from(this._comboOptions).findIndex(opt => 
                opt.getAttribute('aria-selected') === 'true'
            );
            // Default to the first option
            let nextIdx = 0 
            
            if (isDown) {
                nextIdx = currentSelected===-1 || currentSelected + 1 > arrLength -1
                    ? 0
                    : currentSelected + 1   
            } else {
                nextIdx = currentSelected===-1 || currentSelected - 1 < 0
                    ? arrLength - 1
                    : currentSelected - 1
            }
            // Set Classes / Aria attributes
            this.handleFauxFocusOption(this._comboOptions[nextIdx].id);
        }

    }


    handleInputKeyDown(event) {
        console.log('sldsComboboxMulti.js In handleInputKeyDown');
        const ignoreKeys = ['Enter', 'Escape', 'Shift', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
        if (event?.key && ignoreKeys.some(ignore => ignore === event.key)) {
            this._readOnlyStr = '';
            return;
        }
        if (!this._comboOptions) {
            // Query template for the combobox options
            combos = this.querySelectorAll('.slds-listbox__option_entity');
            if (combos) this._comboboxOptions = combos;
        }

        

        // how many ms have passed
        const diffMs = Date.now() - this._readOnlyStrTimeStamp; 
        if (diffMs > 2000) {
            this._readOnlyStr = '';
        }
        

        // Get the options (can be grouped or flat)
        const options = this._groupedOptions ? this.flatGroupedOptions() : this._options;
        
        // Check for user searcheslistBoxOptionClass
        const {nextStr, match, index} = handleReadOnlySearch(event, this._readOnlyStr, options);

        if (typeof nextStr === 'string') {
            this._readOnlyStrTimeStamp = Date.now()
            console.log('sldsComboboxMulti.js Here is the nextStr in handleInputKeyDown(): ', nextStr);
            this._readOnlyStr = nextStr;
            if (match) {
                const combos = this.querySelectorAll('.slds-listbox__option_entity');
                const matchedEle = Array.from(combos).find(ele => ele.dataset.stableId === match.stableId );
                if (matchedEle) {
                    // Remove slds-has-focus from all the options
                    this._comboOptions?.forEach(combo => {
                        combo.classList.remove('slds-has-focus'); 
                        combo.setAttribute('aria-selected', 'false');
                    });
                            
                    matchedEle.classList.add('slds-has-focus');
                    matchedEle.setAttribute('aria-selected', 'true');
                    matchedEle.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); // Ensure visibility
                }
            }
        }
    }

}
